import React from "react";
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Tooltip,
  Divider,
  Chip,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";

// Iconos
import BarChartIcon from "@mui/icons-material/BarChart";
import CodeIcon from "@mui/icons-material/Code";
import InsightsIcon from "@mui/icons-material/Insights"; // Coding Opportunities V2
import FactCheckIcon from "@mui/icons-material/FactCheck";
import DescriptionIcon from "@mui/icons-material/Description";
import PersonIcon from "@mui/icons-material/Person";
import AssignmentIcon from "@mui/icons-material/Assignment";
import MedicationIcon from "@mui/icons-material/Medication";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import ScienceIcon from "@mui/icons-material/Science";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import BugReportIcon from "@mui/icons-material/BugReport";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import EmergencyIcon from "@mui/icons-material/Emergency";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import WarningIcon from "@mui/icons-material/Warning";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import TerminalIcon from "@mui/icons-material/Terminal";
import TravelExploreIcon from "@mui/icons-material/TravelExplore"; // Patient Geography
// Iconos Etapa 2 — BillingV2
import EventNoteIcon from "@mui/icons-material/EventNote";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import SchoolIcon from "@mui/icons-material/School";
import GavelIcon from "@mui/icons-material/Gavel";
import CampaignIcon from "@mui/icons-material/Campaign";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import GroupsIcon from "@mui/icons-material/Groups";
import TimelineIcon from "@mui/icons-material/Timeline";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import MenuBookIcon from "@mui/icons-material/MenuBook"; // Stage 2 Guide

import { SIDEBAR_SECTIONS } from "../../../../config/navigationConfig";
import { useViewAsRole } from "../../../../context/ViewAsRoleContext/ViewAsRoleContext";
import { AuthContext } from "../../../../context/AuthContext/AuthContext";
import { canUserAccessRoute } from "../../../../app/AccessDenied/roleRoutePermissions";
import { useContext } from "react";

const ICON_MAP = {
  BarChartIcon: BarChartIcon,
  CodeIcon: CodeIcon,
  InsightsIcon: InsightsIcon,
  FactCheckIcon: FactCheckIcon,
  DescriptionIcon: DescriptionIcon,
  PersonIcon: PersonIcon,
  AssignmentIcon: AssignmentIcon,
  MedicationIcon: MedicationIcon,
  LocalHospitalIcon: LocalHospitalIcon,
  ScienceIcon: ScienceIcon,
  AdminPanelSettingsIcon: AdminPanelSettingsIcon,
  BugReportIcon: BugReportIcon,
  AssignmentIndIcon: AssignmentIndIcon,
  ExitToAppIcon: ExitToAppIcon,
  EmergencyIcon: EmergencyIcon,
  AttachMoneyIcon: AttachMoneyIcon,
  WarningIcon: WarningIcon,
  VisibilityOffIcon: VisibilityOffIcon,
  CalendarMonthIcon: CalendarMonthIcon,
  ReceiptLongIcon: ReceiptLongIcon,
  TerminalIcon: TerminalIcon,
  TravelExploreIcon: TravelExploreIcon,
  EventNoteIcon: EventNoteIcon,
  MonitorHeartIcon: MonitorHeartIcon,
  EventAvailableIcon: EventAvailableIcon,
  SchoolIcon: SchoolIcon,
  GavelIcon: GavelIcon,
  CampaignIcon: CampaignIcon,
  PersonAddIcon: PersonAddIcon,
  GroupsIcon: GroupsIcon,
  TimelineIcon: TimelineIcon,
  LeaderboardIcon: LeaderboardIcon,
  MenuBookIcon: MenuBookIcon,
};

const NavigationSections = ({ collapsed }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const theme = useTheme();
  const { hasViewAsPermission } = useViewAsRole();
  const { user } = useContext(AuthContext);

  const sidebar = theme.palette.sidebar;
  const currentPath = location.pathname.toLowerCase();

  // Visibilidad de items del sidebar.
  // - Si hay viewAsRole (preview de admin), respetamos los permisos del
  //   rol simulado (lista hardcodeada en PermissionsContext, OK para preview).
  // - Para el usuario real, gateamos por ruta usando user.modules — la misma
  //   fuente que usa RoleGuard. Así, cuando admin activa un módulo nuevo
  //   desde la matriz RBAC, el siguiente login lo refleja automáticamente.
  const isItemVisible = (item) => {
    const viewAsResult = hasViewAsPermission(item.permissions);
    if (viewAsResult !== null) return viewAsResult;
    return canUserAccessRoute(user, item.path);
  };

  const isActive = (itemPath) => {
    return currentPath === itemPath.toLowerCase();
  };

  return (
    <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", py: 1 }}>
      {SIDEBAR_SECTIONS.map((section, sectionIndex) => {
        // Filtrar items visibles. Los subheaders ({ type: "subheader" }) no
        // tienen ruta ni permisos: se conservan solo si el grupo que encabezan
        // (hasta el próximo subheader) tiene al menos un item visible.
        const rawVisible = section.items.filter(
          (item) => item.type === "subheader" || isItemVisible(item)
        );
        const visibleItems = rawVisible.filter((item, idx) => {
          if (item.type !== "subheader") return true;
          const next = rawVisible[idx + 1];
          return Boolean(next) && next.type !== "subheader";
        });

        // Si no hay items navegables visibles, ocultar seccion completa
        if (!visibleItems.some((item) => item.type !== "subheader")) return null;

        return (
          <Box key={section.id}>
            {sectionIndex > 0 && (
              <Divider sx={{ borderColor: sidebar.divider, mx: 2, my: 0.5 }} />
            )}

            {/* Section label */}
            {!collapsed && (
              <Typography
                variant="overline"
                sx={{
                  color: sidebar.sectionLabel,
                  px: 2.5,
                  pt: 1.5,
                  pb: 0.5,
                  display: "block",
                  fontSize: "0.65rem",
                  fontWeight: 700,
                  letterSpacing: "0.1em",
                }}
              >
                {t(section.labelKey, section.labelFallback)}
              </Typography>
            )}

            {collapsed && sectionIndex > 0 && (
              <Box sx={{ my: 0.5 }} />
            )}

            {/* Items */}
            <List disablePadding>
              {visibleItems.map((item) => {
                // Subheader de grupo dentro de la sección (no navegable).
                if (item.type === "subheader") {
                  if (collapsed) {
                    return (
                      <Divider
                        key={item.id}
                        sx={{ borderColor: sidebar.divider, mx: 2, my: 0.75 }}
                      />
                    );
                  }
                  return (
                    <Typography
                      key={item.id}
                      variant="overline"
                      sx={{
                        color: sidebar.sectionLabel,
                        opacity: 0.85,
                        px: 3,
                        pt: 1.25,
                        pb: 0.25,
                        display: "block",
                        fontSize: "0.6rem",
                        fontWeight: 700,
                        letterSpacing: "0.08em",
                      }}
                    >
                      {t(item.labelKey, item.labelFallback)}
                    </Typography>
                  );
                }

                const IconComponent = ICON_MAP[item.icon];
                const active = isActive(item.path);
                const label = t(item.labelKey, item.labelFallback);

                const button = (
                  <ListItemButton
                    key={item.id}
                    onClick={() => navigate(item.path)}
                    sx={{
                      minHeight: 40,
                      px: collapsed ? 2.5 : 2.5,
                      py: 0.6,
                      mx: collapsed ? 0.5 : 1,
                      borderRadius: 1,
                      justifyContent: collapsed ? "center" : "flex-start",
                      borderLeft: active ? "3px solid #00bcd4" : "3px solid transparent",
                      backgroundColor: active ? sidebar.activeItem : "transparent",
                      "&:hover": {
                        backgroundColor: active
                          ? sidebar.activeItem
                          : sidebar.hoverItem,
                      },
                    }}
                  >
                    {IconComponent && (
                      <ListItemIcon
                        sx={{
                          minWidth: collapsed ? 0 : 36,
                          color: active ? "#00bcd4" : item.iconColor || sidebar.text,
                          justifyContent: "center",
                        }}
                      >
                        <IconComponent fontSize="small" />
                      </ListItemIcon>
                    )}
                    {!collapsed && (
                      <ListItemText
                        primary={label}
                        primaryTypographyProps={{
                          fontSize: "0.82rem",
                          fontWeight: active ? 600 : 400,
                          color: active ? sidebar.activeText : sidebar.text,
                          noWrap: true,
                        }}
                      />
                    )}
                    {!collapsed && item.badge && (
                      <Chip
                        label={item.badge}
                        size="small"
                        // "Develop" = legado (ya no se usa; se conserva el color);
                        // "Test" = listo para pruebas de usuario (azul);
                        // cualquier otro badge ("New") conserva el verde.
                        color={
                          item.badge === "Develop"
                            ? "warning"
                            : item.badge === "Test"
                            ? "info"
                            : "success"
                        }
                        sx={{ height: 18, fontSize: "0.6rem", fontWeight: 700, ml: 0.5 }}
                      />
                    )}
                  </ListItemButton>
                );

                if (collapsed) {
                  return (
                    <Tooltip key={item.id} title={label} placement="right" arrow>
                      {button}
                    </Tooltip>
                  );
                }

                return button;
              })}
            </List>
          </Box>
        );
      })}
    </Box>
  );
};

export default NavigationSections;
